import { css } from "@emotion/react";
import { Theme, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import Link from "next/link";
import { useMemo } from "react";
import { match } from "ts-pattern";

import Loading from "~/components/me/bento/Loading";
import RetriableError from "~/components/me/bento/RetriableError";
import ContentBodyLayout from "~/components/me/bento/content/ContentBodyLayout";
import { useMeExamHistory } from "~/components/me/bento/lib";
import { mediaQueryIsMobile } from "~/lib/client/design";
import { MeExamHistoryQuery, Sdk } from "~/lib/graphql/client";

type ResultHistoryItem = MeExamHistoryQuery["me"]["userExamResults"][number];
type PurchaseHistoryItem =
  MeExamHistoryQuery["me"]["squareExamPurchases"][number] & {
    resultHash?: string | null;
  };

type ExamHistoryItem =
  | { kind: "result"; result: ResultHistoryItem }
  | { kind: "purchase"; purchase: PurchaseHistoryItem };

const buildExamHistoryItems = (
  content: MeExamHistoryQuery
): ExamHistoryItem[] => {
  const resultPurchaseIds = new Set(
    content.me.userExamResults
      .map((r) => r.squareExamPurchaseId)
      .filter((id): id is string => typeof id === "string" && id.length > 0)
  );

  const purchasesWithoutResult = content.me.squareExamPurchases.filter(
    (p) => !resultPurchaseIds.has(p.id)
  );

  const items: ExamHistoryItem[] = [
    ...content.me.userExamResults.map((r) => ({
      kind: "result" as const,
      result: r,
    })),
    ...purchasesWithoutResult.map((p) => ({
      kind: "purchase" as const,
      purchase: p,
    })),
  ];

  const toTime = (item: ExamHistoryItem) => {
    if (item.kind === "result") {
      return new Date(
        item.result.completedAt ?? item.result.createdAt
      ).getTime();
    }

    const p = item.purchase;
    const displayDate =
      p.examStatus === "started" ? p.updatedAt : (p.purchasedAt ?? p.createdAt);
    return new Date(displayDate).getTime();
  };

  return items.sort((a, b) => toTime(b) - toTime(a));
};

const ExamHistory = ({ client }: { client: Sdk }) => {
  const result = useMeExamHistory(client);

  return match(result)
    .with({ status: "pending" }, () => <Loading />)
    .with({ status: "error" }, (matched) => (
      <RetriableError reload={matched.refetch} />
    ))
    .with({ status: "success" }, (matched) => <Body content={matched.data} />)
    .exhaustive();
};

const Body = ({ content }: { content: MeExamHistoryQuery }) => {
  const theme = useTheme();
  const sorted = useMemo(() => {
    return buildExamHistoryItems(content);
  }, [content]);

  const hasMore = sorted.length > 0;
  const isEmpty = sorted.length === 0;

  return (
    <ContentBodyLayout label={"Exam History"}>
      <div css={containerStyle}>
        {hasMore && (
          <div css={topBarStyle}>
            <Link href={"/me/exam-history"} css={viewAllLinkStyle(theme)}>
              すべて見る
            </Link>
          </div>
        )}
        <div css={[listStyle, isEmpty && listEmptyStyle]} role={"list"}>
          {isEmpty ? (
            <p css={emptyStyle}>受験履歴がありません</p>
          ) : (
            sorted.map((item) => (
              <HistoryCard
                key={
                  item.kind === "result"
                    ? `result-${item.result.id}`
                    : `purchase-${item.purchase.id}`
                }
                item={item}
              />
            ))
          )}
        </div>
      </div>
    </ContentBodyLayout>
  );
};

const HistoryCard = ({ item }: { item: ExamHistoryItem }) => {
  const theme = useTheme();
  const isResult = item.kind === "result";
  const exam = isResult ? item.result.externalExam : item.purchase.externalExam;
  const categoryId = exam.category?.id;
  const internalId = exam.internalId;

  const examUrl = (() => {
    if (!categoryId) return null;
    if (item.kind === "result") {
      const resultHash = item.result.resultHash;
      return resultHash
        ? `/category/${categoryId}/exam/${internalId}/take#${resultHash}`
        : null;
    }
    if (item.purchase.examStatus === "started") {
      const resultHash = item.purchase.resultHash;
      return resultHash
        ? `/category/${categoryId}/exam/${internalId}/take#${resultHash}`
        : `/category/${categoryId}/exam/${internalId}/take`;
    }
    return `/category/${categoryId}/exam/${internalId}/take`;
  })();

  const badgeVariant = (() => {
    if (item.kind === "result") return "result";
    if (item.purchase.examStatus === "started") return "resume";
    return "pre";
  })();

  const badgeText = (() => {
    if (item.kind === "result") return examUrl ? "結果を見る" : null;
    if (item.purchase.examStatus === "started") return "受験を再開する";
    if (item.purchase.examStatus === "not_started") return "受験前";
    return "受験前";
  })();

  const displayDate = (() => {
    if (item.kind === "result")
      return item.result.completedAt ?? item.result.createdAt;
    const p = item.purchase;
    return p.examStatus === "started"
      ? p.updatedAt
      : (p.purchasedAt ?? p.createdAt);
  })();
  const formattedDate = dayjs(displayDate).format("YYYY/MM/DD HH:mm");

  const CardContent = (
    <div css={cardStyle(theme)}>
      <div css={cardMainStyle}>
        <span css={titleStyle(theme)}>{exam.name}</span>
        <span css={dateStyle(theme)}>{formattedDate}</span>
      </div>
      {badgeText && (
        <span css={badgeStyle(theme, badgeVariant)}>{badgeText}</span>
      )}
    </div>
  );

  if (!examUrl) {
    return (
      <div css={[linkWrapperStyle, disabledLinkStyle]} role={"listitem"}>
        {CardContent}
      </div>
    );
  }

  return (
    <Link href={examUrl} css={linkWrapperStyle} role={"listitem"}>
      {CardContent}
    </Link>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const topBarStyle = css`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.2rem;
`;

const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.34rem;
  margin: 0;
  overflow-y: auto;
  padding-right: 0.2rem;
  ${mediaQueryIsMobile(css`
    gap: 0.2rem;
    max-height: 13rem;
    padding-right: 0.1rem;
  `)}
`;

const listEmptyStyle = css`
  justify-content: center;
  align-items: center;
`;

const emptyStyle = css`
  margin: 0.35rem 0;
  font-size: 0.74rem;
  color: rgba(15, 23, 42, 0.7);
  text-align: center;
`;

const viewAllLinkStyle = (theme: Theme) => css`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.16rem 0.42rem;
  font-size: 0.66rem;
  font-weight: 600;
  color: ${theme.palette.primary.main};
  border-radius: 8px;
  border: 1px solid ${alpha(theme.palette.primary.main, 0.16)};
  background: ${alpha(theme.palette.primary.main, 0.06)};
  text-decoration: none;
  transition:
    background 0.18s ease,
    border-color 0.18s ease;

  &:hover {
    background: ${alpha(theme.palette.primary.main, 0.14)};
    border-color: ${alpha(theme.palette.primary.main, 0.28)};
  }
  ${mediaQueryIsMobile(css`
    padding: 0.12rem 0.32rem;
    font-size: 0.58rem;
  `)}
`;

const linkWrapperStyle = css`
  text-decoration: none;
  color: inherit;
  display: block;
`;

const disabledLinkStyle = css`
  opacity: 0.6;
  pointer-events: none;
`;

const cardStyle = (theme: Theme) => css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.32rem;
  width: 100%;
  padding: 0.45rem 0.65rem;
  border-radius: 12px;
  border: 1px solid ${alpha(theme.palette.text.primary, 0.1)};
  background: ${alpha(theme.palette.common.white, 0.96)};
  box-shadow: 0 6px 14px ${alpha(theme.palette.common.black, 0.06)};
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 20px ${alpha(theme.palette.common.black, 0.12)};
  }

  ${mediaQueryIsMobile(css`
    padding: 0.32rem 0.42rem;
    gap: 0.18rem;
    border-radius: 11px;
  `)}
`;

const cardMainStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.16rem;
  min-width: 0;
  flex: 1;
  ${mediaQueryIsMobile(css`
    gap: 0.12rem;
  `)}
`;

const dateStyle = (theme: Theme) => css`
  font-size: 0.56rem;
  color: ${alpha(theme.palette.text.secondary, 0.82)};
  letter-spacing: 0.04em;
  ${mediaQueryIsMobile(css`
    font-size: 0.5rem;
  `)}
`;

const badgeStyle = (theme: Theme, variant: "result" | "resume" | "pre") => {
  // 見た目（色味）は購入ページの MUI Button 寄せにするが、サイズはマイページ既存のままにする。
  // - result: 「購入する」(contained + primary 相当)
  // - resume: 「受験する」(outlined + primary 相当)
  const primary = theme.palette.primary.main;

  const borderColor =
    variant === "pre" ? alpha(theme.palette.text.secondary, 0.18) : primary;

  const background =
    variant === "result"
      ? primary
      : variant === "resume"
        ? alpha(primary, 0.04)
        : alpha(theme.palette.text.secondary, 0.06);

  const color =
    variant === "result"
      ? theme.palette.primary.contrastText
      : variant === "resume"
        ? primary
        : alpha(theme.palette.text.secondary, 0.92);

  return css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 0.18rem 0.5rem;
    border-radius: 999px;
    border: 1px solid ${borderColor};
    background: ${background};
    color: ${color};
    font-size: 0.6rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: none;
    white-space: nowrap;
    ${mediaQueryIsMobile(css`
      font-size: 0.52rem;
      padding: 0.12rem 0.36rem;
    `)}
  `;
};

const titleStyle = (theme: Theme) => css`
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.25;
  color: ${theme.palette.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${mediaQueryIsMobile(css`
    font-size: 0.8rem;
  `)}
`;

export default ExamHistory;
export { HistoryCard, buildExamHistoryItems };
